import { useState, useEffect, useCallback } from 'react';
import { http } from '../../../api/http-client';
import { useAuth } from '../../../context/AuthContext';

export interface CommentAuthor {
  _id: string;
  name: string;
  username: string;
  email: string;
  avatarUrl?: string;
  online?: boolean;
}

export interface CommentData {
  id: string;
  content: string;
  author: CommentAuthor;
  createdAt: string;
  replies?: CommentData[];
}

interface CommentsState {
  comments: CommentData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const EMPTY_PAGINATION = { page: 1, limit: 20, total: 0, pages: 0 };

export function useComments(blogSlug: string | undefined, postSlug: string | undefined) {
  const [comments, setComments] = useState<CommentData[]>([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchComments = useCallback(
    async (page = 1) => {
      if (!blogSlug || !postSlug) return;
      setLoading(true);
      try {
        const res = await http.get<CommentsState>(`/blog/${blogSlug}/${postSlug}/comments`, {
          params: { page, limit: 20 },
        });
        setComments(res.data.comments);
        setPagination(res.data.pagination);
        setError(null);
      } catch {
        setError('Failed to load comments');
      } finally {
        setLoading(false);
      }
    },
    [blogSlug, postSlug]
  );

  useEffect(() => {
    fetchComments(1);
  }, [fetchComments]);

  const addComment = useCallback(
    async (content: string): Promise<boolean> => {
      if (!blogSlug || !postSlug || !content.trim()) return false;
      setSubmitting(true);
      try {
        const res = await http.post<CommentData>(
          `/blog/${blogSlug}/${postSlug}/comments`,
          { content }
        );
        setComments(prev => [res.data, ...prev]);
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
        return true;
      } catch {
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [blogSlug, postSlug]
  );

  const addReply = useCallback(
    async (commentId: string, content: string): Promise<boolean> => {
      if (!content.trim()) return false;
      setSubmitting(true);
      try {
        const res = await http.post<CommentData>(`/comments/${commentId}/replies`, { content });
        setComments(prev =>
          prev.map(c =>
            c.id === commentId
              ? { ...c, replies: [...(c.replies || []), res.data] }
              : c
          )
        );
        return true;
      } catch {
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  const deleteComment = useCallback(async (commentId: string): Promise<boolean> => {
    try {
      await http.delete(`/comments/${commentId}`);
      // Remove from top-level or from replies
      setComments(prev =>
        prev
          .filter(c => c.id !== commentId)
          .map(c => ({
            ...c,
            replies: (c.replies || []).filter(r => r.id !== commentId),
          }))
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    comments,
    pagination,
    loading,
    submitting,
    error,
    fetchComments,
    addComment,
    addReply,
    deleteComment,
    isAuthenticated,
  };
}
